import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Building, Calendar, RefreshCw, Loader2 } from "lucide-react";
import { ExperienceItem } from "@/hooks/profile";
import { toast } from "sonner";
import { useExperienceDescription } from "./useExperienceDescription";

interface AddExperienceFormProps {
  onSave: (item: ExperienceItem) => void;
  onCancel: () => void;
  disabled?: boolean;
  initialData?: ExperienceItem;
  isEditing?: boolean;
}

const initialExperienceItem: ExperienceItem = {
  id: crypto.randomUUID(),
  position: "",
  company: "",
  start_date: "",
  end_date: "",
  current: false,
  description: "",
  location: "",
};

const AddExperienceForm = ({
  onSave,
  onCancel,
  disabled = false,
  initialData,
  isEditing = false,
}: AddExperienceFormProps) => {
  const [item, setItem] = useState<ExperienceItem>(
    initialData || initialExperienceItem
  );
  const [dateError, setDateError] = useState<string>("");

  const { isGenerating, handleGenerateDescription } = useExperienceDescription(
    item,
    (description) => setItem((prev) => ({ ...prev, description }))
  );

  const validateDateRange = useCallback(() => {
    if (item.start_date && item.end_date && !item.current) {
      const startDate = new Date(item.start_date);
      const endDate = new Date(item.end_date);

      if (endDate < startDate) {
        setDateError("End date cannot be before start date");
        return false;
      }
    }
    setDateError("");
    return true;
  }, [item.start_date, item.end_date, item.current]);

  const handleItemChange = useCallback((updates: Partial<ExperienceItem>) => {
    setItem((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSave = useCallback(() => {
    if (!item.position.trim() || !item.company.trim() || !item.start_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!validateDateRange()) return;

    onSave({
      ...item,
      id: item.id || crypto.randomUUID(),
      end_date: item.current ? null : item.end_date,
    });
  }, [item, onSave, validateDateRange]);

  const isFormValid = Boolean(
    item.position.trim() &&
      item.company.trim() &&
      item.start_date &&
      (!dateError || item.current)
  );

  return (
    <Card className="border-dashed">
      <CardContent className="p-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="position">Position</Label>
          <Input
            id="position"
            value={item.position}
            onChange={(e) => handleItemChange({ position: e.target.value })}
            placeholder="e.g., Senior Designer"
            disabled={disabled}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground">
                <Building className="h-4 w-4" />
              </span>
              <Input
                id="company"
                value={item.company}
                onChange={(e) => handleItemChange({ company: e.target.value })}
                placeholder="Company Name"
                className="rounded-l-none"
                disabled={disabled}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={item.location || ""}
              onChange={(e) => handleItemChange({ location: e.target.value })}
              placeholder="e.g., Remote, Lagos, Nigeria"
              disabled={disabled}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground">
                <Calendar className="h-4 w-4" />
              </span>
              <Input
                id="start_date"
                type="month"
                value={item.start_date}
                onChange={(e) =>
                  handleItemChange({ start_date: e.target.value })
                }
                className="rounded-l-none"
                disabled={disabled}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <Label htmlFor="end_date">End Date</Label>
              <div className="flex items-center space-x-2">
                <Switch
                  id="current-position"
                  checked={item.current}
                  onCheckedChange={(checked) =>
                    handleItemChange({
                      current: checked,
                      end_date: checked ? "" : item.end_date,
                    })
                  }
                  disabled={disabled}
                />
                <Label htmlFor="current-position" className="text-sm">
                  Current
                </Label>
              </div>
            </div>
            <Input
              id="end_date"
              type="month"
              value={item.end_date}
              onChange={(e) => handleItemChange({ end_date: e.target.value })}
              disabled={item.current || disabled}
              placeholder={item.current ? "Present" : ""}
            />
            {dateError && (
              <p className="text-sm text-destructive mt-1">{dateError}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="description">Description</Label>
            <Button
              type="button"
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleGenerateDescription}
              disabled={
                disabled ||
                isGenerating ||
                !item.position ||
                !item.company ||
                !item.start_date
              }
              size="sm"
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Generate Description
            </Button>
          </div>
          <Textarea
            id="description"
            value={item.description || ""}
            onChange={(e) => handleItemChange({ description: e.target.value })}
            placeholder="Describe your responsibilities and achievements..."
            rows={3}
            disabled={disabled || isGenerating}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Fill in position, company, and dates to generate a description
            automatically
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={disabled || !isFormValid}
          >
            {isEditing ? "Update Experience" : "Save Experience"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddExperienceForm;
